import LecturerLayout from '../../components/LecturerLayout';
import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';

// ─── Type definitions ─────────────────────────────────────────────────────────

const TYPES = {
  leave: {
    label:       'Full Day Leave',
    short:       'Full Day Leave',
    color:       'bg-amber-50 text-amber-700 border-amber-200',
    dot:         'bg-amber-400',
    description: 'You are requesting a full working day off. No classes will be scheduled on this date. Please submit this request in advance to allow the academic coordinator to make the necessary arrangements.',
    needsTime:   false,
  },
  'morning half day leave': {
    label:       'Morning Half Day Leave',
    short:       'Morning Half Day',
    color:       'bg-orange-50 text-orange-700 border-orange-200',
    dot:         'bg-orange-400',
    description: 'You are requesting absence for the morning session (approximately 8:30 AM – 12:30 PM). Classes may still be allocated during the afternoon session. Please submit this request in advance so that scheduling adjustments can be made accordingly.',
    needsTime:   false,
  },
  'evening half day leave': {
    label:       'Evening Half Day Leave',
    short:       'Evening Half Day',
    color:       'bg-yellow-50 text-yellow-700 border-yellow-200',
    dot:         'bg-yellow-400',
    description: 'You are requesting absence for the afternoon/evening session (approximately 1:00 PM – 5:00 PM). Classes may still be allocated during the morning session. Please submit this request in advance to allow the academic coordinator to plan accordingly.',
    needsTime:   false,
  },
  unavailability: {
    label:       'Unavailability',
    short:       'Unavailability',
    color:       'bg-rose-50 text-rose-700 border-rose-200',
    dot:         'bg-rose-500',
    description: 'This applies when you are physically present at the institution but temporarily unavailable to conduct classes during a specific time period — for example, due to administrative duties, internal meetings, or other professional commitments. You must specify the exact time range and provide a clear reason.\n\nImportant: This type does not apply to sessions conducted with students or other academic engagements. Such activities are managed directly by the academic coordinator and are reflected as scheduled events in the timetable.',
    needsTime:   true,
  },
};

const TYPE_KEYS = Object.keys(TYPES);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const emptyForm = {
  type:      'leave',
  reason:    '',
  date:      new Date().toISOString().split('T')[0],
  startTime: '',
  endTime:   '',
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (isNaN(d)) return value;
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (time) => {
  if (!time) return '—';
  const parts = String(time).split(':');
  if (parts.length < 2) return time;
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1] ?? '00';
  const suffix = hours >= 12 ? 'PM' : 'AM';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${String(hours).padStart(2, '0')}:${minutes} ${suffix}`;
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function Unavailability() {
  const [records, setRecords]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [formData, setFormData]   = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => { fetchRecords(); }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/lecturer/unavailability');
      setRecords(res.data?.records || res.data || []);
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || 'Failed to load records.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => { setFormData(emptyForm); setShowForm(true); };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const today = new Date().toISOString().split('T')[0];
    if (formData.date < today) {
      Swal.fire('Validation Error', 'You cannot submit a record for a past date.', 'warning');
      return;
    }
    if (!formData.reason.trim()) {
      Swal.fire('Validation Error', 'A reason is required.', 'warning');
      return;
    }
    const needsTime = TYPES[formData.type]?.needsTime;
    if (needsTime && (!formData.startTime || !formData.endTime)) {
      Swal.fire('Validation Error', 'Start time and end time are required for unavailability.', 'warning');
      return;
    }

    const payload = {
      type:      formData.type,
      reason:    formData.reason.trim(),
      date:      formData.date,
      startTime: needsTime ? formData.startTime : null,
      endTime:   needsTime ? formData.endTime   : null,
    };

    setSubmitting(true);
    try {
      await axiosInstance.post('/lecturer/unavailability', payload);
      Swal.fire('Submitted', 'Your record has been added successfully.', 'success');
      setShowForm(false);
      fetchRecords();
    } catch (e) {
      Swal.fire('Error', e?.response?.data?.message || 'Failed to save record.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = filterType === 'all' ? records : records.filter((r) => r.type === filterType);
  const currentTypeMeta = TYPES[formData.type] || TYPES.leave;

  return (
    <LecturerLayout>
      <div className="p-6 max-w-5xl mx-auto">

        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Unavailability &amp; Leave</h1>
            <p className="text-sm text-gray-500 mt-1">
              Submit and manage your leave requests and unavailability notices for academic scheduling purposes.
            </p>
          </div>
          <button
            onClick={openAddForm}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            + Add Record
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filterType === 'all' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}
          >
            All
          </button>
          {TYPE_KEYS.map((key) => (
            <button
              key={key}
              onClick={() => setFilterType(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filterType === key ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}
            >
              {TYPES[key].short}
            </button>
          ))}
        </div>

        {/* Records table */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">Loading records...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No records found.</div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Time</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Reason</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((record, idx) => {
                  const meta = TYPES[record.type] || { label: record.type, color: 'bg-gray-100 text-gray-600 border-gray-200', needsTime: false };
                  return (
                    <tr key={record.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>
                          {meta.label || record.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatDate(record.date)}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {meta.needsTime && record.startTime && record.endTime
                          ? `${formatTime(record.startTime)} – ${formatTime(record.endTime)}`
                          : <span className="text-gray-400">Full / Half Day</span>
                        }
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs truncate" title={record.reason}>
                        {record.reason}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Record Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">Add Unavailability / Leave</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="overflow-y-auto flex-1 px-6 py-5">
              <form onSubmit={handleSubmit} className="space-y-5">

                {/* Type selector */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Record Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  >
                    <option value="leave">Full Day Leave</option>
                    <option value="morning half day leave">Morning Half Day Leave</option>
                    <option value="evening half day leave">Evening Half Day Leave</option>
                    <option value="unavailability">Unavailability (Partial — with time range)</option>
                  </select>
                </div>

                {/* Contextual description box */}
                <div className={`rounded-xl border px-4 py-3.5 ${
                  formData.type === 'unavailability'
                    ? 'bg-rose-50 border-rose-200'
                    : formData.type === 'morning half day leave'
                    ? 'bg-orange-50 border-orange-200'
                    : formData.type === 'evening half day leave'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <div className="flex items-start gap-2.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className={`w-4 h-4 mt-0.5 shrink-0 ${
                      formData.type === 'unavailability' ? 'text-rose-500' : 'text-amber-500'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                    </svg>
                    <div>
                      <p className={`text-xs font-semibold mb-1 ${
                        formData.type === 'unavailability' ? 'text-rose-700' : 'text-amber-700'
                      }`}>
                        {currentTypeMeta.label}
                      </p>
                      {currentTypeMeta.description.split('\n\n').map((para, i) => (
                        <p key={i} className={`text-xs leading-relaxed ${i > 0 ? 'mt-2' : ''} ${
                          formData.type === 'unavailability' ? 'text-rose-600' : 'text-amber-700'
                        }`}>
                          {para}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  />
                </div>

                {/* Start / End time — only for unavailability */}
                {currentTypeMeta.needsTime && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Start Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="startTime"
                        value={formData.startTime}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        End Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        name="endTime"
                        value={formData.endTime}
                        onChange={handleChange}
                        required
                        className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                    </div>
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Please provide a clear and specific reason…"
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                  />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-5 py-2 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-60"
                  >
                    {submitting ? 'Submitting…' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </LecturerLayout>
  );
}
